import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { provideNgxMask } from 'ngx-mask';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { PageScrollSpyComponent, ScrollSpySection } from '../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { MaskService } from '../../../shared/services/mask.service';
import { UtilService } from '../../../shared/services/util.service';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { TipoPessoaEnum } from '../../../shared/enums/pessoa/tipo-pessoa.enum';
import { ServicoTomadoService } from '../../../shared/services/apis/repo-dfse/servico-tomado.service';
import { TagServicoTomadoService } from '../../../shared/services/apis/repo-dfse/tag-servico-tomado.service';
import { ServicoTomadoLookupService } from '../../../shared/services/apis/repo-dfse/servico-tomado-lookup.service';
import { ServicoTomadoCreateRequest } from '../../../shared/services/requests/repo-dfse/servico-tomado-create.request';
import { ServicoTomadoCreateResponse } from '../../../shared/services/responses/repo-dfse/servico-tomado-create.response';
import { ServicoTomadoDetalheStruct } from '../../../shared/services/structs/repo-dfse/servico-tomado-detalhe.struct';
import { TagServicoTomadoItemStruct } from '../../../shared/services/structs/repo-dfse/tag-servico-tomado-item.struct';
import { StatusFiscalNFSeEntradaLookupStruct } from '../../../shared/services/structs/repo-dfse/status-fiscal-nfse-entrada-lookup.struct';
import { StatusFiscalNFSeEntradaEnum } from '../../../shared/enums/repo-dfse/status-fiscal-nfse-entrada.enum';

import { ServicoTomadoDadosCabecalhoComponent } from './components/servico-tomado-dados-cabecalho/servico-tomado-dados-cabecalho.component';
import { ServicoTomadoEmitenteComponent } from './components/servico-tomado-emitente/servico-tomado-emitente.component';
import { ServicoTomadoServicosComponent } from './components/servico-tomado-servicos/servico-tomado-servicos.component';
import { ServicoTomadoImpostosRetidosComponent } from './components/servico-tomado-impostos-retidos/servico-tomado-impostos-retidos.component';
import { ServicoTomadoImpostosCreditadosComponent } from './components/servico-tomado-impostos-creditados/servico-tomado-impostos-creditados.component';
import { ServicoTomadoTotaisComponent } from './components/servico-tomado-totais/servico-tomado-totais.component';
import { ServicoTomadoPagamentoComponent } from './components/servico-tomado-pagamento/servico-tomado-pagamento.component';
import { ServicoTomadoDadosAdicionaisComponent } from './components/servico-tomado-dados-adicionais/servico-tomado-dados-adicionais.component';

@Component({
  selector: 'app-servico-tomado-cadastro',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule,
    HeaderComponent, PageScrollSpyComponent, FooterActionComponent,
    ServicoTomadoDadosCabecalhoComponent, ServicoTomadoEmitenteComponent, ServicoTomadoServicosComponent,
    ServicoTomadoImpostosRetidosComponent, ServicoTomadoImpostosCreditadosComponent, ServicoTomadoTotaisComponent,
    ServicoTomadoPagamentoComponent, ServicoTomadoDadosAdicionaisComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './servico-tomado-cadastro.component.html',
  styleUrl: './servico-tomado-cadastro.component.css',
})
export class ServicoTomadoCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_ServicosTomados;

  public form!: FormGroup;
  public isEditMode = false;
  public isDup = false;
  public idNFSeEntrada: number | null = null;
  public isLoading = false;
  public isSaving = false;
  public listaTags: TagServicoTomadoItemStruct[] = [];
  public listaStatusFiscal: StatusFiscalNFSeEntradaLookupStruct[] = [];

  public readonly percentageMask = this.maskService.getDecimalMask2();
  public readonly dynamicCurrencyMask = this.maskService.getCurrencyMask(2);

  public sections: ScrollSpySection[] = [
    { id: 'cabecalho', label: 'Serviço Tomado' },
    { id: 'emitente', label: 'Remetente' },
    { id: 'servicos', label: 'Serviços' },
    { id: 'impostos-retidos', label: 'Impostos Retidos' },
    { id: 'impostos-creditados', label: 'Impostos Creditados' },
    { id: 'totais', label: 'Totais' },
    { id: 'pagamento', label: 'Pagamento' },
    { id: 'dados-adicionais', label: 'Dados Adicionais' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private servicoTomadoService: ServicoTomadoService,
    private tagServicoTomadoService: TagServicoTomadoService,
    private servicoTomadoLookupService: ServicoTomadoLookupService,
    private alertService: AlertService,
    private maskService: MaskService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.buildForm();

    this.route.params.subscribe(params => {
      if (params['id']) {
        this.idNFSeEntrada = Number(params['id']);
        this.isEditMode = true;
        this.carregarDetalhe(this.idNFSeEntrada);
      }
    });

    this.route.queryParams.subscribe(queryParams => {
      if (queryParams['clonarDe']) {
        this.carregarParaClonagem(Number(queryParams['clonarDe']));
      }
    });

    this.form.get('valorServico')?.valueChanges.subscribe(() => this.recalcularTotais());
    ['irValor', 'inssValor', 'issValor', 'pisRetidoValor', 'cofinsRetidoValor', 'csllRetidoValor'].forEach(campo => {
      this.form.get(campo)?.valueChanges.subscribe(() => this.recalcularTotais());
    });

    this.carregarTags();
    this.carregarStatusFiscal();
  }

  private carregarStatusFiscal(): void {
    this.servicoTomadoLookupService.getStatusFiscal().subscribe(res => {
      if (!res.isError) {
        this.listaStatusFiscal = res.list || [];
      }
    });
  }

  private carregarTags(): void {
    this.tagServicoTomadoService.getAll('', 1, 500).subscribe(res => {
      if (!res.isError) {
        this.listaTags = (res.listaTags || []).map(t => ({ id: t.idTagServicoTomado, descricao: t.descricao, cor: t.cor }));
      }
    });
  }

  private carregarTagsVinculadas(idNFSeEntrada: number): void {
    this.servicoTomadoService.getTags(idNFSeEntrada).subscribe(res => {
      if (!res.isError) {
        this.form.get('tags')?.setValue((res.list || []).map(t => t.idTagServicoTomado), { emitEvent: false });
      }
    });
  }

  private buildForm(): void {
    this.form = this.formBuilder.group({
      // Cabeçalho
      numeroNFSe: ['', Validators.required],
      serieDps: [''],
      chaveAcesso: [''],
      idStatusFiscalNFSeEntrada: [StatusFiscalNFSeEntradaEnum.Regular, Validators.required],
      datetimeEmissaoDps: [new Date(), Validators.required],
      dataExecucaoEntrada: [new Date()],

      // Emitente (TCEmitente do XSD RTC v1.01 — só nacional, sem Insc. Estadual)
      idPessoa: [null, Validators.required],
      tipoPessoaEmitente: [TipoPessoaEnum.Juridica],
      identificacaoFiscal: [''],
      nomeEmitente: ['', Validators.required],
      nomeFantasia: [''],
      inscricaoMunicipal: [''],
      telefone: [''],
      email: [''],
      cep: [''],
      logradouro: ['', Validators.required],
      numeroEndereco: ['', Validators.required],
      complemento: [''],
      bairro: ['', Validators.required],
      municipioNome: ['', Validators.required],
      codigoMunicipioIbge: [''],
      uf: ['', Validators.required],

      // Serviços
      descricaoServico: ['', Validators.required],
      valorServico: [0, Validators.required],
      codigoNaturezaBaseCalculoCreditoSped: [null],
      codigoOrigemCreditoSped: [null],

      // Impostos Retidos
      irBase: [null], irAliquota: [null], irValor: [null],
      inssBase: [null], inssAliquota: [null], inssValor: [null],
      issBase: [null], issAliquota: [null], issValor: [null],
      pisRetidoAliquota: [null], pisRetidoValor: [null],
      cofinsRetidoAliquota: [null], cofinsRetidoValor: [null],
      csllRetidoAliquota: [null], csllRetidoValor: [null],

      // Impostos Creditados
      pisCreditadoCst: [null], pisCreditadoBase: [null], pisCreditadoAliquota: [null], pisCreditadoValor: [null],
      cofinsCreditadoCst: [null], cofinsCreditadoBase: [null], cofinsCreditadoAliquota: [null], cofinsCreditadoValor: [null],

      // Totais
      valorTotal: [0],
      valorLiquido: [0],

      // Pagamento
      condicaoPagamento: ['', Validators.required],
      idCategoriaFinanceira: [null],
      // Sem parcelas a nota fica sem previsão de desembolso e não há o que lançar em
      // Contas a Pagar depois — por isso ao menos uma é exigida.
      parcelas: this.formBuilder.array([], Validators.required),

      // Dados Adicionais
      observacaoInterna: [''],
      tags: [[]],
    });
  }

  private recalcularTotais(): void {
    const valorServico = Number(this.form.get('valorServico')?.value) || 0;
    const retidos = ['irValor', 'inssValor', 'issValor', 'pisRetidoValor', 'cofinsRetidoValor', 'csllRetidoValor']
      .reduce((soma, campo) => soma + (Number(this.form.get(campo)?.value) || 0), 0);

    // Arredondamento ABNT (NBR 5891), como no resto do sistema — Math.round divergiria no 0,5.
    this.form.get('valorTotal')?.setValue(UtilService.arredondarAbnt(valorServico, 2), { emitEvent: false });
    this.form.get('valorLiquido')?.setValue(UtilService.arredondarAbnt(valorServico - retidos, 2), { emitEvent: false });
  }

  private carregarDetalhe(id: number): void {
    this.isLoading = true;
    this.servicoTomadoService.getById(id).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.isError || !res.detalhe) {
          this.alertService.show('Erro', res.errorDescription || 'Serviço tomado não encontrado.', AlertType.error);
          this.router.navigate(['/suprimentos/servicos-tomados']);
          return;
        }

        this.aplicarDetalhe(res.detalhe);
        this.carregarTagsVinculadas(id);
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Falha ao carregar o serviço tomado.', AlertType.error);
      }
    });
  }

  /**
   * Clonagem: carrega os dados do documento de origem no formulário, mas SEM marcar isEditMode
   * nem gravar idNFSeEntrada — assim salvar() sempre faz POST (criação), nunca PUT sobre o antigo.
   */
  private carregarParaClonagem(idOrigem: number): void {
    this.isLoading = true;
    this.servicoTomadoService.getById(idOrigem).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.isError || !res.detalhe) {
          this.alertService.show('Erro', res.errorDescription || 'Serviço tomado de origem não encontrado.', AlertType.error);
          return;
        }

        this.aplicarDetalhe(res.detalhe);
        this.carregarTagsVinculadas(idOrigem);
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Falha ao carregar os dados para clonagem.', AlertType.error);
      }
    });
  }

  private aplicarDetalhe(d: ServicoTomadoDetalheStruct): void {
    this.form.patchValue({
      numeroNFSe: d.numeroNFSe,
      serieDps: d.serieDps,
      chaveAcesso: d.chaveAcesso,
      idStatusFiscalNFSeEntrada: d.idStatusFiscalNFSeEntrada,
      datetimeEmissaoDps: d.datetimeEmissaoDps ? new Date(d.datetimeEmissaoDps) : null,
      dataExecucaoEntrada: this.converterDataApiParaDate(d.dataExecucaoEntrada),
      idPessoa: d.idPessoa,
      tipoPessoaEmitente: d.tipoPessoaEmitente,
      // Persistido sem separadores; o campo exibe e guarda o valor formatado.
      identificacaoFiscal: MaskService.formatDocumento(d.identificacaoFiscal || ''),
      nomeEmitente: d.nomeEmitente,
      nomeFantasia: d.nomeFantasia,
      inscricaoMunicipal: d.inscricaoMunicipal,
      telefone: d.telefone,
      email: d.email,
      logradouro: d.logradouro,
      numeroEndereco: d.numeroEndereco,
      complemento: d.complemento,
      bairro: d.bairro,
      codigoMunicipioIbge: d.codigoMunicipioIbge,
      municipioNome: d.municipioNome,
      uf: d.uf,
      cep: d.cep,
      descricaoServico: d.descricaoServico,
      valorServico: d.valorServico,
      codigoNaturezaBaseCalculoCreditoSped: d.codigoNaturezaBaseCalculoCreditoSped,
      codigoOrigemCreditoSped: d.codigoOrigemCreditoSped,
      irBase: d.irBase, irAliquota: d.irAliquota, irValor: d.irValor,
      inssBase: d.inssBase, inssAliquota: d.inssAliquota, inssValor: d.inssValor,
      issBase: d.issBase, issAliquota: d.issAliquota, issValor: d.issValor,
      pisRetidoAliquota: d.pisRetidoAliquota, pisRetidoValor: d.pisRetidoValor,
      cofinsRetidoAliquota: d.cofinsRetidoAliquota, cofinsRetidoValor: d.cofinsRetidoValor,
      csllRetidoAliquota: d.csllRetidoAliquota, csllRetidoValor: d.csllRetidoValor,
      pisCreditadoCst: d.pisCreditado?.codigoCst, pisCreditadoBase: d.pisCreditado?.baseCalculo,
      pisCreditadoAliquota: d.pisCreditado?.aliquota, pisCreditadoValor: d.pisCreditado?.valor,
      cofinsCreditadoCst: d.cofinsCreditado?.codigoCst, cofinsCreditadoBase: d.cofinsCreditado?.baseCalculo,
      cofinsCreditadoAliquota: d.cofinsCreditado?.aliquota, cofinsCreditadoValor: d.cofinsCreditado?.valor,
      valorTotal: d.valorTotal,
      valorLiquido: d.valorLiquido,
      condicaoPagamento: d.condicaoPagamento,
      idCategoriaFinanceira: d.idCategoriaFinanceira,
      observacaoInterna: d.observacaoInterna,
    }, { emitEvent: false });

    const parcelas = this.form.get('parcelas') as FormArray;
    while (parcelas.length) parcelas.removeAt(0);
    (d.parcelas || []).forEach(p => {
      parcelas.push(this.formBuilder.group({
        numeroParcela: [p.numeroParcela],
        dataVencimento: [this.converterDataApiParaDate(p.dataVencimento)],
        valorParcela: [p.valorParcela],
      }));
    });
  }

  private montarRequest(): ServicoTomadoCreateRequest {
    const v = this.form.getRawValue();
    const request = new ServicoTomadoCreateRequest();

    request.numeroNFSe = v.numeroNFSe;
    request.serieDps = v.serieDps;
    request.chaveAcesso = v.chaveAcesso || null;
    request.idStatusFiscalNFSeEntrada = v.idStatusFiscalNFSeEntrada;
    request.datetimeEmissaoDps = new Date(v.datetimeEmissaoDps).toISOString();
    request.dataExecucaoEntrada = this.converterDateParaDataApi(v.dataExecucaoEntrada);

    request.idPessoa = v.idPessoa;
    request.tipoPessoaEmitente = v.tipoPessoaEmitente;
    // O controle guarda o documento formatado; grava-se apenas os caracteres do documento.
    request.identificacaoFiscal = (v.identificacaoFiscal || '').replace(/[.\-\/]/g, '') || null;
    request.nomeEmitente = v.nomeEmitente;
    request.nomeFantasia = v.nomeFantasia;
    request.inscricaoMunicipal = v.inscricaoMunicipal;
    request.logradouro = v.logradouro;
    request.numeroEndereco = v.numeroEndereco;
    request.complemento = v.complemento;
    request.bairro = v.bairro;
    request.codigoMunicipioIbge = v.codigoMunicipioIbge;
    request.municipioNome = v.municipioNome;
    request.uf = v.uf;
    request.cep = v.cep;
    request.telefone = v.telefone;
    request.email = v.email;

    request.descricaoServico = v.descricaoServico;
    request.valorServico = v.valorServico;
    request.codigoNaturezaBaseCalculoCreditoSped = v.codigoNaturezaBaseCalculoCreditoSped;
    request.codigoOrigemCreditoSped = v.codigoOrigemCreditoSped;

    request.irBase = v.irBase; request.irAliquota = v.irAliquota; request.irValor = v.irValor;
    request.inssBase = v.inssBase; request.inssAliquota = v.inssAliquota; request.inssValor = v.inssValor;
    request.issBase = v.issBase; request.issAliquota = v.issAliquota; request.issValor = v.issValor;
    request.pisRetidoAliquota = v.pisRetidoAliquota; request.pisRetidoValor = v.pisRetidoValor;
    request.cofinsRetidoAliquota = v.cofinsRetidoAliquota; request.cofinsRetidoValor = v.cofinsRetidoValor;
    request.csllRetidoAliquota = v.csllRetidoAliquota; request.csllRetidoValor = v.csllRetidoValor;

    request.pisCreditado = v.pisCreditadoCst || v.pisCreditadoBase ? {
      codigoCst: v.pisCreditadoCst, baseCalculo: v.pisCreditadoBase, aliquota: v.pisCreditadoAliquota, valor: v.pisCreditadoValor
    } : null;
    request.cofinsCreditado = v.cofinsCreditadoCst || v.cofinsCreditadoBase ? {
      codigoCst: v.cofinsCreditadoCst, baseCalculo: v.cofinsCreditadoBase, aliquota: v.cofinsCreditadoAliquota, valor: v.cofinsCreditadoValor
    } : null;

    request.valorTotal = v.valorTotal;
    request.valorLiquido = v.valorLiquido;

    request.condicaoPagamento = v.condicaoPagamento;
    request.idCategoriaFinanceira = v.idCategoriaFinanceira;
    request.parcelas = (v.parcelas || []).map((p: any) => ({
      numeroParcela: p.numeroParcela,
      dataVencimento: this.converterDateParaDataApi(p.dataVencimento)!,
      valorParcela: p.valorParcela,
    }));

    request.observacaoInterna = v.observacaoInterna;

    return request;
  }

  /**
   * `dataExecucaoEntrada` e `dataVencimento` sao colunas `date`. Montar/ler a data por componentes evita
   * o deslocamento de um dia que o parse ISO causaria em UTC-03 (`new Date('2026-10-20')` cai em 19/10 as 21h locais).
   */
  private converterDateParaDataApi(data: Date | null): string | null {
    if (!data) return null;
    const valor = new Date(data);
    if (isNaN(valor.getTime())) return null;
    const ano = valor.getFullYear();
    const mes = `${valor.getMonth() + 1}`.padStart(2, '0');
    const dia = `${valor.getDate()}`.padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  private converterDataApiParaDate(valor: string | null | undefined): Date | null {
    if (!valor) return null;
    const [ano, mes, dia] = valor.substring(0, 10).split('-').map(Number);
    if (!ano || !mes || !dia) return null;
    return new Date(ano, mes - 1, dia);
  }

  public salvar(): void {
    // A seção Pagamento fica no fim da página: sem uma mensagem específica, o usuário levaria
    // o aviso genérico de campos obrigatórios sem enxergar o que está faltando.
    const parcelas = this.form.get('parcelas') as FormArray;
    if (!parcelas.length) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Informe a condição de pagamento e gere as parcelas antes de salvar.', AlertType.warning);
      return;
    }

    const somaParcelas = parcelas.controls.reduce((soma, c) => soma + (Number(c.get('valorParcela')?.value) || 0), 0);
    const valorLiquido = Number(this.form.get('valorLiquido')?.value) || 0;
    if (Math.abs(somaParcelas - valorLiquido) > 0.01) {
      this.alertService.show('Atenção',
        `A soma das parcelas (${somaParcelas.toFixed(2)}) não confere com o valor líquido (${valorLiquido.toFixed(2)}).`,
        AlertType.warning);
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Preencha os campos obrigatórios.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const request = this.montarRequest();
    const tags: number[] = this.form.get('tags')?.value || [];

    const obs = this.isEditMode && this.idNFSeEntrada
      ? this.servicoTomadoService.update(this.idNFSeEntrada, request)
      : this.servicoTomadoService.create(request);

    obs.subscribe({
      next: (res) => {
        if (res.isError) {
          this.isSaving = false;
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }

        const idNFSeEntrada = this.isEditMode && this.idNFSeEntrada
          ? this.idNFSeEntrada
          : (res as ServicoTomadoCreateResponse).idNFSeEntrada;

        this.servicoTomadoService.updateTags(idNFSeEntrada, tags).subscribe({
          next: () => {
            this.isSaving = false;
            this.alertService.show('Sucesso', 'Serviço Tomado registrado com sucesso!', AlertType.success);
            this.router.navigate(['/suprimentos/servicos-tomados']);
          },
          error: () => {
            this.isSaving = false;
            this.alertService.show('Atenção', 'Serviço Tomado salvo, mas houve falha ao atualizar as tags.', AlertType.warning);
            this.router.navigate(['/suprimentos/servicos-tomados']);
          }
        });
      },
      error: (err) => {
        this.isSaving = false;
        this.alertService.show('Erro inesperado', err.message || err, AlertType.error);
      }
    });
  }

  public cancelar(): void {
    this.router.navigate(['/suprimentos/servicos-tomados']);
  }
}
